import { useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { LoadingButton } from '@mui/lab';
import { useMembershipApi } from '../api';
import { useAlert } from '@lendica/components';
import { useData } from '../contexts/useData';

export interface DeleteDialogProps {
    open: boolean;
    invitationId: string;
    handleClose: () => void;
}

export default function DeleteDialog({ open, invitationId, handleClose }: DeleteDialogProps) {
    const [loading, setLoading] = useState(false);
    const { setNeutralMessage, setErrorMessage } = useAlert();
    const { refreshInvitations } = useData();
    const membershipApi = useMembershipApi();

    const handleDelete = async () => {
        setLoading(true);
        try {
            if (!invitationId) throw new Error('Invitation ID is required');
            const res = await membershipApi.revokeInvitationById(invitationId);
            setNeutralMessage(res.message || 'Invitation deleted');

            await refreshInvitations(true);
            setTimeout(() => {
                setLoading(false);
                handleClose();
            }, 1500);
        } catch (error: any) {
            const message =
                typeof error?.message === 'string' && error.message.length > 0
                    ? error.message
                    : 'Failed to delete invitation';
            setErrorMessage(message);
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            PaperProps={{
                sx: {
                    paddingY: 1,
                    paddingX: 1,
                },
            }}
        >
            <DialogTitle id="alert-dialog-title">
                Are you sure you want to delete this invitation?
            </DialogTitle>
            <DialogContent>
                <DialogContentText id="alert-dialog-description" sx={{ whiteSpace: 'pre-line' }}>
                    {`This will remove the invite from the table, and invalidate it immediately if it's still pending`}{' '}
                    <i>
                        (In this case, the recipient won’t be able to use the link that's already
                        been sent).
                    </i>{' '}
                    <br />
                    {`You can add this person later anytime, you’ll just need to send a new invite`}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="secondary">
                    Cancel
                </Button>
                <LoadingButton
                    loading={loading}
                    onClick={handleDelete}
                    autoFocus
                    variant="contained"
                    color="error"
                    sx={{
                        'backgroundColor': 'error.dark',
                        'color': 'white',
                        '&:hover': {
                            backgroundColor: 'error.main',
                        },
                    }}
                >
                    Delete invitation
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}
